import { useState, useEffect } from "react";
import {
  Home,
  Calendar,
  Video,
  Presentation,
  BarChart2,
  Music,
  Settings,
  ChevronRight,
  LogIn,
  LogOut,
  Menu,
  Radio,
  X,
  HandHeart,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import logo from "../assets/images/praise sawa  (1).svg";

const Sidebar = () => {
  const { t, i18n } = useTranslation();
  const [activeMenuItem, setActiveMenuItem] = useState("Home");
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1400
  );
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("adminToken")
  );
  const [isSidebarOpen, setSidebarOpen] = useState(windowWidth >= 1400); // Open by default on large screens (≥ 1400px)
  const navigate = useNavigate();
  const location = useLocation();

  const isRtl = i18n.language === "ar";
  const isLargeScreen = windowWidth >= 1400; // Show sidebar without toggle on large screens (≥ 1400px)

  const menuItems = [
    {
      name: "Home",
      id: "",
      icon: Home,
      description: t("sidebar.menu_items.Home.description"),
      gradient: "from-slate-500 to-gray-500",
    },
    {
      name: "Live Now",
      id: "live-worship",
      icon: Radio,
      description: t("sidebar.menu_items.Live Now.description"),
      gradient: "from-slate-500 to-gray-500",
    },
    {
      name: "Conferences",
      id: "conferences",
      icon: Presentation,
      description: t("sidebar.menu_items.Conferences.description"),
      gradient: "from-slate-500 to-gray-500",
    },
    {
      name: "Tutorials",
      id: "Tutorial",
      icon: Video,
      description: t("sidebar.menu_items.Tutorials.description"),
      gradient: "from-slate-500 to-gray-500",
    },
    // Add this new donation item
    {
      name: "Support Us",
      id: "support",
      icon: HandHeart,
      description: t("sidebar.menu_items.Support Us.description"),
      gradient: "from-slate-500 to-gray-500",
    },
  ];

  const adminItems = [
    {
      name: "Overview",
      id: "admin-overview",
      icon: BarChart2,
      description: t("sidebar.admin_items.Overview.description"),
      gradient: "from-slate-500 to-gray-500",
    },
    {
      name: "Songs",
      id: "admin/songs",
      icon: Music,
      description: t("sidebar.admin_items.Songs.description"),
      gradient: "from-slate-500 to-gray-500",
    },
    {
      name: "Settings",
      id: "admin/settings",
      icon: Settings,
      description: t("sidebar.admin_items.Settings.description"),
      gradient: "from-slate-500 to-gray-500",
    },
    {
      name: "Logout",
      id: "logout",
      icon: LogOut,
      description: t("sidebar.admin_items.Logout.description"),
      gradient: "from-red-500 to-red-700",
    },
  ];

  const adminLoginItem = {
    name: "Admin Login",
    id: "admin",
    icon: LogIn,
    description: t("sidebar.admin_items.Admin Login.description"),
    gradient: "from-slate-500 to-gray-500",
  };

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 1400); // Open sidebar on large screens, closed on smaller
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initialize on mount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update activeMenuItem based on current route and authentication state
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    setIsAuthenticated(!!token);

    const currentPath = location.pathname.substring(1);
    const matchedItem = [...menuItems, ...adminItems, adminLoginItem].find(
      (item) =>
        item.id === currentPath || (item.id === "" && location.pathname === "/")
    );
    if (matchedItem) {
      setActiveMenuItem(matchedItem.name);
    } else {
      setActiveMenuItem("Home");
    }
  }, [location]);

  // Handle menu item click
  const handleMenuClick = async (name, id) => {
    if (id === "logout") {
      try {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/logout`);
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminRole");
        setIsAuthenticated(false);
        setActiveMenuItem("Home");
        navigate("/admin");
        window.location.reload(); // Reload the page after logout
      } catch (err) {
        console.error("Logout error:", err);
      }
    } else {
      setActiveMenuItem(name);
      navigate(`/${id}`);
      // Close sidebar after navigation on mobile or tablet
      if (!isLargeScreen) {
        setSidebarOpen(false);
      }
    }
  };

  // Toggle sidebar visibility on mobile or tablet
  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  // Render a menu item with enhanced styling
  const renderMenuItem = (item, isAdminItem = false) => {
    const isActive = activeMenuItem === item.name;
    const isHovered = hoveredItem === item.name;
    const IconComponent = item.icon;

    return (
      <div
        key={item.name}
        className={`
          flex items-center mb-4 cursor-pointer relative
          transition-all duration-300 ease-in-out 
          ${isActive ? (isRtl ? "-translate-x-2" : "translate-x-2") : isRtl ? "hover:-translate-x-1" : "hover:translate-x-1"}
          group
        `}
        onClick={() => handleMenuClick(item.name, item.id)}
        onMouseEnter={() => setHoveredItem(item.name)}
        onMouseLeave={() => setHoveredItem(null)}
      >
        {(isHovered || isActive) && (
          <div
            className={`absolute -inset-2 bg-gradient-to-r ${item.gradient} opacity-10 rounded-lg blur-sm -z-10`}
          ></div>
        )}

        <div
          className={`
            w-10 h-10 rounded-md flex items-center justify-center
            transition-all duration-300 min-w-[2.5rem] max-w-[2.5rem]
            ${isActive
              ? "bg-stone-50 shadow-md shadow-stone-50/20"
              : "bg-stone-300/80 group-hover:bg-stone-300/90"
            }
          `}
        >
          <IconComponent
            size={20}
            className={`
              transition-all duration-300
              ${isActive
                ? "text-stone-900"
                : "text-stone-900/80 group-hover:text-stone-900"
              }
            `}
          />
        </div>

        <div className={`flex flex-col ${isRtl ? "mr-4" : "ml-4"}`}>
          <span
            className={`
              text-base font-normal font-['MuseoModerno'] transition-all duration-300
              ${isActive
                ? "text-white"
                : "opacity-70 text-stone-300/90 group-hover:opacity-90 group-hover:text-stone-100"
              }
            `}
          >
            {t(`sidebar.${isAdminItem ? "admin_items" : "menu_items"}.${item.name}.name`)}
          </span>

          <span
            className={`
              text-xs text-stone-400 transition-all duration-300 
              ${isActive || isHovered
                ? "opacity-70 max-h-8"
                : "opacity-0 max-h-0"
              } overflow-hidden
            `}
          >
            {item.description}
          </span>
        </div>

        {isActive && (
          <ChevronRight
            size={16}
            className={`${isRtl ? "mr-auto" : "ml-auto"} text-stone-300/80 ${isRtl ? "rotate-180" : ""}`}
          />
        )}
      </div>
    );
  };

  // Sidebar container classes with responsive behavior
  const sidebarClasses = `
    fixed top-0 ${isRtl ? "right-0" : "left-0"} z-40 h-full w-72 flex flex-col
    ${isRtl ? "dir-rtl" : ""}
    transition-transform duration-300 ease-in-out
    ${isSidebarOpen ? "translate-x-0" : isRtl ? "translate-x-full" : "-translate-x-full"}
  `;

  // Toggle button classes
  const toggleButtonClasses = `
    fixed top-40 ${isRtl ? "right-0 rounded-tl-xl rounded-bl-xl" : "left-0 rounded-tr-xl rounded-br-xl"} z-50
    w-10 h-10 bg-stone-900 shadow-lg text-white
    flex items-center justify-center
    transition-all duration-300 ease-in-out
    ${isSidebarOpen ? (isRtl ? "right-[288px]" : "left-[288px]") : ""}
  `;

  // Add overlay for mobile and tablet when sidebar is open
  const overlayClasses = `
    fixed inset-0 bg-white z-30
    transition-opacity duration-300 ease-in-out
    ${isSidebarOpen && !isLargeScreen ? "opacity-100" : "opacity-0 pointer-events-none"}
  `;

  return (
    <>
      {/* Toggle Button for Mobile and Tablet */}
      {!isLargeScreen && (
        <button
          className={toggleButtonClasses}
          onClick={toggleSidebar}
          aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
        >
          {isSidebarOpen ? (
            <X size={24} className="text-white" />
          ) : (
            <Menu size={24} className="text-white" />
          )}
        </button>
      )}

      {/* Backdrop Overlay for Mobile and Tablet */}
      <div className={overlayClasses} onClick={() => setSidebarOpen(false)}></div>

      {/* Sidebar */}
      <div className={sidebarClasses} dir={isRtl ? "rtl" : "ltr"}>
        {/* Logo Section with white background */}
        <div className="bg-white">
          <div className="w-36 h-32 mx-auto relative flex items-center justify-center">
            <img
              src={logo}
              alt={t("sidebar.worship_praise")}
              className="max-h-full transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        </div>
        {/* Main Content */}
        <div
          className={`
            w-full flex-1 bg-stone-900 ${isRtl ? "rounded-tl-3xl" : "rounded-tr-3xl"}
            px-6 py-6 overflow-y-auto scrollbar-thin scrollbar-thumb-stone-700 scrollbar-track-stone-900
          `}
        >
          <div className="mb-10">
            <div
              className={`opacity-70 text-stone-50 text-xs font-medium mb-6 ${isRtl ? "mr-2" : "ml-2"
                } font-['MuseoModerno'] uppercase tracking-wider`}
            >
              {t("sidebar.menu")}
            </div>
            <div className="space-y-5">
              {menuItems.map((item) => renderMenuItem(item))}
            </div>
          </div>
          <div>
            <div
              className={`opacity-70 text-stone-50 text-xs font-medium mb-6 ${isRtl ? "mr-2" : "ml-2"
                } font-['MuseoModerno'] uppercase tracking-wider`}
            >
              {isAuthenticated ? t("sidebar.admin") : t("sidebar.admin_access")}
            </div>
            <div className="space-y-5">
              {isAuthenticated
                ? adminItems.map((item) => renderMenuItem(item, true))
                : renderMenuItem(adminLoginItem, true)}
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-stone-800/50">
            <div className="flex flex-col items-center space-y-2">
              <div className="text-xs text-stone-400 text-center">
                {t("sidebar.worship_praise")}
              </div>
              <div className="text-xs text-stone-500 text-center">
                <a href={`mailto:${t("footer.email")}`} className="hover:underline">
                  {t("footer.email")}
                </a>
              </div>
              <div className="text-xs text-stone-600">
                {t("sidebar.copyright", { year: new Date().getFullYear() })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;